import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Send, MoreHorizontal, MessageCircle } from 'lucide-react'
import type { DirectMessage } from '@olimhub/shared'
import { messagesApi, accountsApi } from '@/api'
import { Avatar } from '@/components/profile/Avatar'
import { formatDate, formatPostTime } from '@/lib/dates'
import { useAuthStore } from '@/store/auth.store'
import { useToast } from '@/components/common/Toast'
import { autoLinkUrls } from '@/lib/markdown'

function getDayKey(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

function getDayLabel(iso: string, today: string, yesterday: string): string {
  const d = new Date(iso)
  const now = new Date()
  const yest = new Date(now)
  yest.setDate(now.getDate() - 1)
  if (d.toDateString() === now.toDateString()) return today
  if (d.toDateString() === yest.toDateString()) return yesterday
  return formatDate(iso)
}

interface MessageBubbleProps {
  msg: DirectMessage
  isOwn: boolean
  onDelete: () => void
}

function MessageBubble({ msg, isOwn, onDelete }: MessageBubbleProps) {
  const { t } = useTranslation()
  const [hovered, setHovered] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Auto-dismiss confirm after 3s
  useEffect(() => {
    if (!confirmDelete) return
    const t = setTimeout(() => setConfirmDelete(false), 3000)
    return () => clearTimeout(t)
  }, [confirmDelete])

  // Close dropdown on outside click
  useEffect(() => {
    if (!showDropdown) return
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showDropdown])

  const handleTouchStart = () => {
    if (!isOwn || msg.isDeleted) return
    longPressTimer.current = setTimeout(() => {
      setShowDropdown(true)
    }, 500)
  }

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const handleConfirm = () => {
    setShowDropdown(false)
    setConfirmDelete(true)
  }

  const handleDelete = () => {
    setConfirmDelete(false)
    onDelete()
  }

  if (msg.isDeleted) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isOwn ? 'flex-end' : 'flex-start',
          marginBottom: 4,
        }}
      >
        <div
          style={{
            maxWidth: '75%',
            padding: '8px 12px',
            borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-sm)',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
          }}
        >
          {t('messages.message_deleted')}
        </div>
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 10,
            color: 'var(--text-faint)',
            marginTop: 2,
            marginInlineEnd: isOwn ? 4 : 0,
            marginInlineStart: isOwn ? 0 : 4,
          }}
        >
          {formatPostTime(msg.createdAt)}
        </span>
      </div>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isOwn ? 'flex-end' : 'flex-start',
        marginBottom: 4,
      }}
      onMouseEnter={() => isOwn && setHovered(true)}
      onMouseLeave={() => { if (!showDropdown) setHovered(false) }}
    >
      {/* Bubble row with optional three-dot button.
          maxWidth lives on the ROW so the bubble fills it naturally;
          putting maxWidth on the bubble itself shrinks it when the
          row has no intrinsic width (flex-end child in a column). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexDirection: isOwn ? 'row-reverse' : 'row', maxWidth: '80%' }}>
        {/* Bubble */}
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: autoLinkUrls(msg.content ?? '') }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          style={{
            flex: 1,
            minWidth: 0,
            padding: '8px 12px',
            borderRadius: isOwn ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            background: isOwn ? 'var(--brand)' : 'var(--surface)',
            color: isOwn ? '#fff' : 'var(--text-strong)',
            border: isOwn ? 'none' : '1px solid var(--border)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-sm)',
            lineHeight: 'var(--lh-normal)',
            wordBreak: 'break-word',
            userSelect: 'none',
          }}
        />

        {/* Three-dot button — own messages, desktop hover */}
        {isOwn && (hovered || showDropdown) && (
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setShowDropdown((v) => !v)}
              style={{
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                padding: 4,
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-muted)',
                display: 'flex',
              }}
            >
              <MoreHorizontal size={15} />
            </button>
            {showDropdown && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  insetInlineEnd: 0,
                  marginBottom: 4,
                  background: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  boxShadow: 'var(--shadow-modal)',
                  minWidth: 160,
                  zIndex: 100,
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  onClick={handleConfirm}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '10px 14px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-sm)',
                    color: '#DC2626',
                    textAlign: 'start',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-2)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none' }}
                >
                  {t('messages.delete_message')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 10,
          color: 'var(--text-faint)',
          marginTop: 2,
          marginInlineEnd: isOwn ? 4 : 0,
          marginInlineStart: isOwn ? 0 : 4,
        }}
      >
        {formatPostTime(msg.createdAt)}
      </span>

      {/* Inline confirm */}
      {confirmDelete && (
        <div
          style={{
            marginTop: 6,
            padding: '8px 12px',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)', whiteSpace: 'nowrap' }}>
            {t('messages.delete_confirm')}
          </span>
          <button
            type="button"
            onClick={() => setConfirmDelete(false)}
            style={{
              padding: '4px 10px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'none',
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-xs)',
              cursor: 'pointer',
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            style={{
              padding: '4px 10px',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              background: '#DC2626',
              color: '#fff',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-xs)',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('post.delete')}
          </button>
        </div>
      )}
    </div>
  )
}

export function ThreadPage() {
  const { id: username } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { account } = useAuthStore()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [allMessages, setAllMessages] = useState<DirectMessage[]>([])
  const [olderCursor, setOlderCursor] = useState<string | null>(null)
  const [hasOlder, setHasOlder] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [inputText, setInputText] = useState('')
  const [sending, setSending] = useState(false)

  const scrollRef = useRef<HTMLDivElement>(null)
  const endRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const isFirstLoad = useRef(true)
  const knownIds = useRef(new Set<string>())

  // Pre-fill from URL params (on mount only)
  useEffect(() => {
    const ref = searchParams.get('ref')
    const type = searchParams.get('type')
    if (!ref) return
    if (type === 'bazaar') {
      setInputText(t('messages.pre_filled_bazaar', { title: searchParams.get('title') ?? '' }))
    } else if (type === 'help') {
      setInputText(t('messages.pre_filled_help', { category: searchParams.get('category') ?? '' }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data: otherProfile } = useQuery({
    queryKey: ['profile', username],
    queryFn: () => accountsApi.getByUsername(username!),
    enabled: !!username,
  })

  const { data: latestData } = useQuery({
    queryKey: ['thread', username],
    queryFn: () => messagesApi.getThread(username!),
    enabled: !!username,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  })

  function isAtBottom(): boolean {
    const el = scrollRef.current
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight < 100
  }

  function scrollToBottom(behavior: ScrollBehavior = 'smooth') {
    endRef.current?.scrollIntoView({ behavior })
  }

  useEffect(() => {
    if (!latestData) return
    const fetched = [...latestData.items].reverse() // server returns DESC; display ASC

    if (isFirstLoad.current) {
      isFirstLoad.current = false
      fetched.forEach((m) => knownIds.current.add(m.id))
      setAllMessages(fetched)
      setOlderCursor(latestData.nextCursor)
      setHasOlder(latestData.hasMore)
      requestAnimationFrame(() => scrollToBottom('instant' as ScrollBehavior))
      void queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] })
      void queryClient.invalidateQueries({ queryKey: ['conversations'] })
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
      return
    }

    // On poll: update isDeleted for messages that were deleted
    setAllMessages((prev) =>
      prev.map((m) => {
        const updated = fetched.find((f) => f.id === m.id)
        if (updated && updated.isDeleted && !m.isDeleted) return updated
        return m
      }),
    )

    const newMsgs = fetched.filter((m) => !knownIds.current.has(m.id))
    if (newMsgs.length === 0) return
    newMsgs.forEach((m) => knownIds.current.add(m.id))
    const atBottom = isAtBottom()
    setAllMessages((prev) =>
      [...prev, ...newMsgs].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    )
    if (atBottom) requestAnimationFrame(() => scrollToBottom())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestData])

  async function loadOlder() {
    if (!olderCursor || loadingOlder) return
    setLoadingOlder(true)
    try {
      const data = await messagesApi.getThread(username!, olderCursor)
      const older = [...data.items].reverse()
      const newOnes = older.filter((m) => !knownIds.current.has(m.id))
      newOnes.forEach((m) => knownIds.current.add(m.id))
      setAllMessages((prev) => [...newOnes, ...prev])
      setOlderCursor(data.nextCursor)
      setHasOlder(data.hasMore)
    } catch {
      toast.error('Could not load older messages')
    } finally {
      setLoadingOlder(false)
    }
  }

  const handleDeleteMessage = useCallback(async (msgId: string) => {
    // Optimistic update
    setAllMessages((prev) =>
      prev.map((m) => (m.id === msgId ? { ...m, isDeleted: true, content: null } : m)),
    )
    try {
      await messagesApi.deleteMessage(msgId)
    } catch {
      // Revert on error
      setAllMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, isDeleted: false } : m)),
      )
      toast.error(t('messages.delete_failed'))
    }
  }, [toast, t])

  async function handleSend() {
    const text = inputText.trim()
    if (!text || sending || !username) return
    setSending(true)
    setInputText('')
    if (textareaRef.current) textareaRef.current.style.height = '36px'
    const optimistic: DirectMessage = {
      id: `opt-${Date.now()}`,
      senderId: account!.id,
      recipientId: '',
      content: text,
      isDeleted: false,
      readAt: null,
      createdAt: new Date().toISOString(),
    }
    knownIds.current.add(optimistic.id)
    setAllMessages((prev) => [...prev, optimistic])
    requestAnimationFrame(() => scrollToBottom())
    try {
      const real = await messagesApi.send(username, text)
      knownIds.current.add(real.id)
      knownIds.current.delete(optimistic.id)
      setAllMessages((prev) => prev.map((m) => (m.id === optimistic.id ? real : m)))
    } catch {
      knownIds.current.delete(optimistic.id)
      setAllMessages((prev) => prev.filter((m) => m.id !== optimistic.id))
      setInputText(text)
      toast.error('Failed to send message')
    } finally {
      setSending(false)
    }
  }

  // Group messages by calendar day
  const grouped: Array<{ dayKey: string; label: string; messages: DirectMessage[] }> = []
  for (const msg of allMessages) {
    const dk = getDayKey(msg.createdAt)
    const last = grouped[grouped.length - 1]
    if (last && last.dayKey === dk) {
      last.messages.push(msg)
    } else {
      grouped.push({
        dayKey: dk,
        label: getDayLabel(msg.createdAt, t('messages.today'), t('messages.yesterday')),
        messages: [msg],
      })
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 16px',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={() => navigate('/messages')}
          style={{
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: 'var(--text-muted)',
            padding: 4,
            display: 'flex',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          <ArrowLeft size={22} />
        </button>
        <button
          type="button"
          onClick={() => navigate(`/@${username}`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          <Avatar
            name={otherProfile?.displayName || (username ?? '')}
            avatarUrl={otherProfile?.avatarUrl ?? null}
            animalAvatar={otherProfile?.animalAvatar ?? null}
            size="sm"
          />
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 700,
              fontSize: 'var(--fs-body)',
              color: 'var(--text-strong)',
            }}
          >
            @{username}
          </span>
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {hasOlder && (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
            <button
              type="button"
              onClick={loadOlder}
              disabled={loadingOlder}
              style={{
                border: '1px solid var(--border)',
                background: 'var(--surface)',
                borderRadius: 'var(--radius-pill)',
                padding: '6px 16px',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-xs)',
                color: 'var(--text-muted)',
                cursor: loadingOlder ? 'default' : 'pointer',
              }}
            >
              {loadingOlder ? t('feed.loading_more') : t('feed.load_more')}
            </button>
          </div>
        )}

        {allMessages.length === 0 && !isFirstLoad.current && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              margin: 'auto',
              textAlign: 'center',
              color: 'var(--text-faint)',
            }}
          >
            <MessageCircle size={40} strokeWidth={1.5} />
            <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)' }}>
              {t('messages.empty_thread')}
            </p>
          </div>
        )}

        {grouped.map(({ dayKey, label, messages: dayMsgs }) => (
          <div key={dayKey}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                margin: '12px 0 8px',
              }}
            >
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--fs-xs)',
                  color: 'var(--text-faint)',
                  flexShrink: 0,
                }}
              >
                {label}
              </span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
            {dayMsgs.map((msg) => (
              <MessageBubble
                key={msg.id}
                msg={msg}
                isOwn={msg.senderId === account?.id}
                onDelete={() => handleDeleteMessage(msg.id)}
              />
            ))}
          </div>
        ))}

        <div ref={endRef} />
      </div>

      {/* Send input */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 10,
          padding: '10px 16px',
          paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface)',
          flexShrink: 0,
        }}
      >
        <Avatar
          name={account?.displayName || account?.username || ''}
          avatarUrl={account?.avatarUrl}
          animalAvatar={account?.animalAvatar}
          size="sm"
        />
        <textarea
          ref={textareaRef}
          value={inputText}
          onChange={(e) => {
            setInputText(e.target.value)
            e.target.style.height = 'auto'
            e.target.style.height = `${Math.min(e.target.scrollHeight, 96)}px`
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void handleSend()
            }
          }}
          placeholder={t('messages.type_a_message')}
          rows={1}
          maxLength={2000}
          style={{
            flex: 1,
            resize: 'none',
            padding: '8px 12px',
            borderRadius: 'var(--radius-md)',
            border: '1.5px solid var(--border)',
            background: 'var(--bg-sunk)',
            color: 'var(--text-strong)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-sm)',
            lineHeight: 'var(--lh-normal)',
            outline: 'none',
            overflowY: 'auto',
            height: 36,
            maxHeight: 96,
            boxSizing: 'border-box',
          }}
        />
        <button
          type="button"
          aria-label={t('messages.send')}
          onClick={() => void handleSend()}
          disabled={!inputText.trim() || sending}
          style={{
            border: 'none',
            background: !inputText.trim() || sending ? 'var(--surface-2)' : 'var(--brand)',
            color: !inputText.trim() || sending ? 'var(--text-faint)' : '#fff',
            cursor: !inputText.trim() || sending ? 'not-allowed' : 'pointer',
            borderRadius: 'var(--radius-pill)',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'background var(--dur-fast)',
          }}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  )
}
